'use client';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { ArrowUpRight, ChevronDown, ChevronRight, Facebook, Instagram, Linkedin, Mail, MapPin, Menu, Minus, Music2, Phone, Plus, Podcast, Twitter, X, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { LandingFooter, LandingNav, LandingNavLink, LandingTheme } from '../../_lib/schema/landing-config.schema';
import { useWebVenuePicker } from '../venue-picker/web-venue-picker';
import { pickLocalized } from './landing-context';
import { contrastForeground, landingTypographyStyle } from './landing-design';
import './landing.module.css';
import styles from './sports-site-chrome.module.css';

type SportsChromeProps = {
  nav: LandingNav;
  businessName?: string | null;
  logoUrl?: string | null;
  locale?: string;
  disableNavigation?: boolean;
  fontFamily?: LandingTheme['fontFamily'];
};

function ChromeLink({ href, children, className, disabled, onClick }: {
  href: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link href={href} prefetch={disabled ? false : undefined} className={className} aria-disabled={disabled || undefined} onClick={(event) => {
      if (disabled) event.preventDefault();
      onClick?.();
    }}>
      {children}
    </Link>
  );
}

function Brand({ nav, logoUrl, businessName, disableNavigation }: SportsChromeProps) {
  return (
    <ChromeLink href="/" className={styles.brand} disabled={disableNavigation}>
      {logoUrl ? (
        <Image src={logoUrl} alt={nav.logo?.alt || businessName || ''} width={220} height={72} unoptimized className={styles.logo} />
      ) : <span>{businessName}</span>}
    </ChromeLink>
  );
}

type NavChild = NonNullable<LandingNavLink['children']>[number];

/** Consecutive children sharing a `group` become one headed column; ungrouped ones stay flat. */
function groupChildren(children: NavChild[]) {
  const groups: { heading?: string; headingAr?: string; links: NavChild[] }[] = [];
  for (const child of children) {
    const last = groups.at(-1);
    if (last && last.heading === child.group) last.links.push(child);
    else groups.push({ heading: child.group, headingAr: child.group_ar, links: [child] });
  }
  return groups;
}

function VenueSwitcher({ language }: { language: string }) {
  const { t } = useTranslation();
  const picker = useWebVenuePicker();
  if (!picker) return null;
  const name = picker.activeVenue ? pickLocalized(picker.activeVenue.name, picker.activeVenue.nameAlt ?? undefined, language) : undefined;
  return (
    <button type="button" className={styles.venueSwitcher} onClick={picker.open} data-test="sports-venue-switcher">
      <MapPin size={16} aria-hidden="true" />
      <span>{name ?? t('marketing:sportsChrome.chooseLocation')}</span>
      <ChevronDown size={14} aria-hidden="true" />
      {name ? <span className="sr-only">{t('marketing:sportsChrome.changeLocation')}</span> : null}
    </button>
  );
}

export function SportsSiteHeader({ nav, businessName, logoUrl, locale, disableNavigation, accountControl, languageControl, memberNavigation, accent = '#2563eb', accentForeground, fontFamily }: SportsChromeProps & {
  accountControl?: ReactNode;
  languageControl?: ReactNode;
  memberNavigation?: ReactNode;
  accent?: string;
  accentForeground?: string;
}) {
  const { t, i18n } = useTranslation();
  const language = locale ?? i18n.language;
  const id = useId();
  const header = useRef<HTMLElement>(null);
  const [state, setState] = useState({ menuOpen: false, group: -1 });
  const label = (link: LandingNavLink) => pickLocalized(link.label, link.label_ar, language) ?? link.label;
  const close = () => setState({ menuOpen: false, group: -1 });
  const announcement = nav.announcement ? pickLocalized(nav.announcement.text, nav.announcement.text_ar, language)?.trim() : undefined;
  const mobileActionLabel = nav.primaryAction ? pickLocalized(nav.primaryAction.mobileLabel, nav.primaryAction.mobileLabel_ar, language) : undefined;

  useEffect(() => {
    const element = header.current;
    const container = element?.parentElement;
    if (!element || !container || typeof ResizeObserver === 'undefined') return;
    const previous = container.style.getPropertyValue('--site-header-h');
    const overlay = nav.headerStyle === 'overlay';
    const measure = () => {
      const height = `${element.getBoundingClientRect().height}px`;
      container.style.setProperty('--site-header-h', height);
      if (overlay) container.style.setProperty('--overlay-header-h', height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (previous) container.style.setProperty('--site-header-h', previous);
      else container.style.removeProperty('--site-header-h');
      container.style.removeProperty('--overlay-header-h');
    };
  }, [nav.headerStyle]);

  return (
    <>
    <header ref={header} className={styles.header} data-presentation={nav.presentation} data-font-family={fontFamily} data-link-case={nav.linkCase} data-style={nav.headerStyle} style={{ ...landingTypographyStyle(fontFamily), '--sports-accent': nav.primaryActionColor ?? accent, '--sports-accent-foreground': nav.primaryActionColor ? contrastForeground(nav.primaryActionColor) : accentForeground ?? contrastForeground(accent), '--sports-menu-accent': accent } as CSSProperties} data-test="sports-site-header" onKeyDown={(event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      const target = state.group >= 0 ? `group-${state.group}` : 'menu';
      header.current?.querySelector<HTMLButtonElement>(`[data-sports-trigger="${target}"]`)?.focus();
      setState(state.group >= 0 ? { ...state, group: -1 } : { menuOpen: false, group: -1 });
    }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) close();
    }}>
      <div className={styles.headerRow} data-presentation={nav.presentation}>
        <Brand nav={nav} logoUrl={logoUrl} businessName={businessName} disableNavigation={disableNavigation} />
        <button type="button" className={styles.menuToggle} data-sports-trigger="menu" aria-expanded={state.menuOpen} aria-controls={`${id}-navigation`} aria-label={t('marketing:sportsChrome.menu')} onClick={() => setState({ menuOpen: !state.menuOpen, group: -1 })}>
          {state.menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <nav id={`${id}-navigation`} className={styles.navigation} data-open={state.menuOpen} aria-label={t('marketing:sportsChrome.navigation')}>
          {nav.links.map((link, index) => (
            <div className={styles.navItem} key={`${link.href}-${index}`}>
              {link.children?.length ? (
                <>
                  <button type="button" className={styles.groupTrigger} data-sports-trigger={`group-${index}`} aria-expanded={state.group === index} aria-controls={`${id}-group-${index}`} onClick={() => setState({ ...state, group: state.group === index ? -1 : index })}>
                    {label(link)} <ChevronDown size={14} aria-hidden="true" />
                  </button>
                  <div id={`${id}-group-${index}`} className={styles.dropPanel} hidden={state.group !== index}>
                    <ChromeLink href={link.href} disabled={disableNavigation} onClick={close} className={styles.groupOverview}>
                      {label(link)} <ArrowUpRight size={24} aria-hidden="true" />
                    </ChromeLink>
                    <div className={styles.childLinks} data-grouped={link.children.some((child) => child.group) || undefined}>
                      {groupChildren(link.children).map((group, groupIndex) => {
                        const links = group.links.map((child, childIndex) => <ChromeLink key={`${child.href}-${childIndex}`} href={child.href} disabled={disableNavigation} onClick={close}>{label(child)} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink>);
                        if (!group.heading) return links;
                        return <div key={`group-${groupIndex}`} className={styles.childGroup}>
                          <p className={styles.childGroupHeading}>{pickLocalized(group.heading, group.headingAr, language) ?? group.heading}</p>
                          {links}
                        </div>;
                      })}
                    </div>
                  </div>
                </>
              ) : <ChromeLink href={link.href} className={styles.navLink} disabled={disableNavigation} onClick={close}>{label(link)}</ChromeLink>}
            </div>
          ))}
          {nav.primaryAction ? <ChromeLink href={nav.primaryAction.href} className={`${styles.primaryAction} ${styles.mobilePrimaryAction}`} disabled={disableNavigation} onClick={close}>{mobileActionLabel ?? label(nav.primaryAction)} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink> : null}
        </nav>
        <div className={styles.actions}>
          {nav.venueSwitcher ? <VenueSwitcher language={language} /> : null}
          {languageControl}
          {accountControl}
          {nav.primaryAction ? <ChromeLink href={nav.primaryAction.href} className={styles.primaryAction} disabled={disableNavigation} onClick={close}>{nav.presentation === 'therapy' ? <><span data-action-label={mobileActionLabel ? 'desktop' : undefined}>{label(nav.primaryAction)}</span>{mobileActionLabel ? <span data-action-label="mobile">{mobileActionLabel}</span> : null}<ChevronRight size={8} aria-hidden="true" /></> : <>{label(nav.primaryAction)} <ArrowUpRight size={16} aria-hidden="true" /></>}</ChromeLink> : null}
        </div>
      </div>
      {memberNavigation}
    </header>
    {/* Outside the sticky header so it scrolls away with the page. */}
    {announcement ? (
      <div className={styles.announcement} style={{ '--sports-accent': accent, '--sports-accent-foreground': accentForeground ?? contrastForeground(accent) } as CSSProperties} data-test="sports-site-announcement">
        {nav.announcement?.href
          ? <ChromeLink href={nav.announcement.href} disabled={disableNavigation}>{announcement}</ChromeLink>
          : <p>{announcement}</p>}
      </div>
    ) : null}
    </>
  );
}

type FooterContact = { address?: string | null; city?: string | null; country?: string | null; phone?: string | null; email?: string | null };

type SportsFooterProps = SportsChromeProps & {
  contact?: FooterContact | null;
  legalControls?: ReactNode;
  /** The visitor's selected court_location; scopes location-tagged social links. */
  activeLocationId?: string | null;
  promoImageUrl?: string | null;
};

export function SportsSiteFooter(props: SportsFooterProps) {
  if (props.nav.footer?.layout === 'rich') return <RichSiteFooter {...props} footer={props.nav.footer} />;
  return <StandardSiteFooter {...props} />;
}

function StandardSiteFooter({ nav, businessName, logoUrl, locale, disableNavigation, contact, legalControls, fontFamily }: SportsFooterProps) {
  const { t, i18n } = useTranslation();
  const language = locale ?? i18n.language;
  const cityCountry = [contact?.city, contact?.country].filter(Boolean).join(', ');
  const label = (link: LandingNavLink) => pickLocalized(link.label, link.label_ar, language) ?? link.label;

  return (
    <footer className={styles.footer} style={landingTypographyStyle(fontFamily)} data-font-family={fontFamily} data-test="sports-site-footer">
      <div className={styles.footerInner}>
        <div className={styles.footerBrand}>
          <Brand nav={nav} logoUrl={logoUrl} businessName={businessName} disableNavigation={disableNavigation} />
          {contact?.address ? <p>{contact.address}</p> : null}
          {cityCountry ? <p>{cityCountry}</p> : null}
          {contact?.phone ? <ChromeLink href={`tel:${contact.phone}`} disabled={disableNavigation}>{contact.phone}</ChromeLink> : null}
          {contact?.email ? <ChromeLink href={`mailto:${contact.email}`} disabled={disableNavigation}>{contact.email}</ChromeLink> : null}
          <ChromeLink href="/contact" disabled={disableNavigation}>{t('marketing:footer.contactUs')} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink>
        </div>
        <nav className={styles.footerGroups} aria-label={t('marketing:sportsChrome.footerNavigation')}>
          {nav.links.filter((link) => link.children?.length).map((link, index) => <div className={styles.footerGroup} key={`${link.href}-${index}`}>
            <ChromeLink href={link.href} className={styles.footerHeading} disabled={disableNavigation}>{label(link)}</ChromeLink>
            {link.children?.map((child, childIndex) => <ChromeLink key={`${child.href}-${childIndex}`} href={child.href} disabled={disableNavigation}>{label(child)}</ChromeLink>)}
          </div>)}
          {/* Single links would each become a heading with nothing under it; group them in one column instead. */}
          {nav.links.some((link) => !link.children?.length) ? <div className={styles.footerGroup}>
            <span className={styles.footerHeading}>{t('marketing:sportsChrome.explore')}</span>
            {nav.links.filter((link) => !link.children?.length).map((link, index) => <ChromeLink key={`${link.href}-${index}`} href={link.href} disabled={disableNavigation}>{label(link)}</ChromeLink>)}
          </div> : null}
        </nav>
        <div className={styles.footerGroup}>
          <span className={styles.footerHeading}>{t('marketing:legal.label')}</span>
          <ChromeLink href="/privacy-policy" disabled={disableNavigation}>{t('marketing:privacyPolicy')}</ChromeLink>
          <ChromeLink href="/terms-of-service" disabled={disableNavigation}>{t('marketing:termsOfService')}</ChromeLink>
          <ChromeLink href="/cookie-policy" disabled={disableNavigation}>{t('marketing:cookiePolicy')}</ChromeLink>
          {legalControls}
        </div>
      </div>
      <div className={styles.footerBottom}>
        <span>{businessName ? t('marketing:copyright', { product: businessName, year: new Date().getFullYear() }) : null}</span>
        <span>{t('marketing:footer.poweredBy')}</span>
      </div>
    </footer>
  );
}

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, tiktok: Music2, linkedin: Linkedin, youtube: Youtube, x: Twitter, spotify: Podcast };
const SOCIAL_LABELS = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', youtube: 'YouTube', x: 'X', spotify: 'Spotify' };

function FooterHref({ href, className, disabled, children }: { href: string; className?: string; disabled?: boolean; children: ReactNode }) {
  return /^https?:\/\//i.test(href)
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={className} aria-disabled={disabled || undefined} onClick={(event) => { if (disabled) event.preventDefault(); }}>{children}</a>
    : <ChromeLink href={href} className={className} disabled={disabled}>{children}</ChromeLink>;
}

function RichSiteFooter({ nav, footer, businessName, logoUrl, locale, disableNavigation, contact, legalControls, fontFamily, activeLocationId, promoImageUrl }: SportsFooterProps & { footer: LandingFooter }) {
  const { t, i18n } = useTranslation();
  const locationsId = useId();
  const language = locale ?? i18n.language;
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, language);
  const label = (link: LandingNavLink) => local(link.label, link.label_ar) ?? link.label;
  const tagline = local(nav.brand?.footerText, nav.brand?.footerText_ar);
  const socials = (footer.socials ?? []).filter((social) => !social.locationId || social.locationId === activeLocationId);
  const links = nav.footerLinks ?? nav.links;
  const place = [contact?.address, contact?.city].filter(Boolean).join(', ');
  const copyright = local(nav.brand?.copyright, nav.brand?.copyright_ar);
  const { promo, disclaimer } = footer;
  const locationsHeading = local(footer.locationsHeading, footer.locationsHeading_ar);
  const promoContent = promo ? <div className={styles.footerPromo} data-test="sports-footer-promo">
    {promoImageUrl ? <Image src={promoImageUrl} alt="" width={160} height={64} unoptimized className={styles.footerPromoImage} /> : null}
    <p className={styles.footerHeading}>{local(promo.title, promo.title_ar)}</p>
    {promo.body ? <p>{local(promo.body, promo.body_ar)}</p> : null}
    {promo.href ? <FooterHref href={promo.href} disabled={disableNavigation}>{t('marketing:sportsChrome.learnMore')} <ArrowUpRight size={16} aria-hidden="true" /></FooterHref> : null}
  </div> : null;

  return (
    <footer className={styles.footer} data-layout="rich" data-presentation={nav.presentation} style={landingTypographyStyle(fontFamily)} data-font-family={fontFamily} data-test="sports-site-footer">
      <div className={styles.footerInner} data-layout="rich">
        <div className={styles.footerBrand}>
          <Brand nav={nav} logoUrl={logoUrl} businessName={businessName} disableNavigation={disableNavigation} />
          {tagline ? <p className={styles.footerTagline}>{tagline}</p> : null}
          {socials.length ? <ul className={styles.socials} aria-label={t('marketing:sportsChrome.socialLinks')}>
            {socials.map((social, index) => {
              const Icon = SOCIAL_ICONS[social.platform];
              return <li key={`${social.platform}-${index}`}><FooterHref href={social.href} disabled={disableNavigation}><Icon size={18} aria-hidden="true" /><span className="sr-only">{SOCIAL_LABELS[social.platform]}</span></FooterHref></li>;
            })}
          </ul> : null}
          {nav.presentation === 'therapy' ? promoContent : null}
        </div>
        {nav.presentation !== 'therapy' ? promoContent : null}
        <nav className={styles.footerGroup} aria-label={t('marketing:sportsChrome.footerNavigation')}>
          <span className={styles.footerHeading}>{local(footer.linksHeading, footer.linksHeading_ar) || t('marketing:sportsChrome.explore')}</span>
          {links.map((link, index) => <ChromeLink key={`${link.href}-${index}`} href={link.href} disabled={disableNavigation}>{label(link)}</ChromeLink>)}
        </nav>
        <div className={styles.footerGroup}>
          <span className={styles.footerHeading}>{t('marketing:legal.label')}</span>
          <ChromeLink href="/privacy-policy" disabled={disableNavigation}>{t('marketing:privacyPolicy')}</ChromeLink>
          <ChromeLink href="/terms-of-service" disabled={disableNavigation}>{t('marketing:termsOfService')}</ChromeLink>
          <ChromeLink href="/cookie-policy" disabled={disableNavigation}>{t('marketing:cookiePolicy')}</ChromeLink>
          {legalControls}
        </div>
      </div>
      {footer.locations?.length ? <section className={styles.footerLocations} data-test="sports-footer-locations" aria-labelledby={locationsHeading ? `${locationsId}-heading` : undefined}>
        {locationsHeading ? <h2 id={`${locationsId}-heading`} className={styles.footerHeading}>{locationsHeading}</h2> : null}
        {footer.locations.map((location, index) => {
          const title = local(location.title, location.title_ar) ?? location.title;
          const address = local(location.address, location.address_ar);
          return <details key={index} name={locationsId} className={styles.footerLocation} data-test="sports-footer-location">
            <summary><Plus className={styles.locationPlus} size={17} aria-hidden="true" /><Minus className={styles.locationMinus} size={17} aria-hidden="true" />{title}</summary>
            <address className={styles.footerLocationContact}>
              {location.phone ? <ChromeLink href={`tel:${location.phone.replace(/[^\d+]/g, '')}`} disabled={disableNavigation}><Phone size={20} aria-hidden="true" /><span dir="ltr">{location.phone}</span></ChromeLink> : null}
              {location.email ? <ChromeLink href={`mailto:${location.email}`} disabled={disableNavigation}><Mail size={20} aria-hidden="true" /><span>{location.email}</span></ChromeLink> : null}
              {address ? location.mapsHref ? <FooterHref href={location.mapsHref} disabled={disableNavigation}><MapPin size={20} aria-hidden="true" /><span>{address}</span></FooterHref> : <span><MapPin size={20} aria-hidden="true" /><span>{address}</span></span> : null}
            </address>
          </details>;
        })}
      </section> : null}
      {contact?.phone || contact?.email || place ? <address className={styles.footerContactStrip} data-test="sports-footer-contact">
        {contact?.phone ? <ChromeLink href={`tel:${contact.phone}`} disabled={disableNavigation}><Phone size={18} aria-hidden="true" /><span dir="ltr">{contact.phone}</span></ChromeLink> : null}
        {contact?.email ? <ChromeLink href={`mailto:${contact.email}`} disabled={disableNavigation}><Mail size={18} aria-hidden="true" /><span>{contact.email}</span></ChromeLink> : null}
        {place ? <span><MapPin size={18} aria-hidden="true" />{place}</span> : null}
      </address> : null}
      {disclaimer ? <div className={styles.footerDisclaimer}>
        <p className={styles.footerHeading}>{local(disclaimer.title, disclaimer.title_ar)}</p>
        <p>{local(disclaimer.body, disclaimer.body_ar)}</p>
        {disclaimer.href ? <ChromeLink href={disclaimer.href} disabled={disableNavigation}>{local(disclaimer.linkLabel, disclaimer.linkLabel_ar) || t('marketing:sportsChrome.learnMore')}</ChromeLink> : null}
      </div> : null}
      <div className={styles.footerBottom}>
        <span>{copyright ? `© ${new Date().getFullYear()} ${copyright}` : businessName ? t('marketing:copyright', { product: businessName, year: new Date().getFullYear() }) : null}</span>
        <span>{t('marketing:footer.poweredBy')}</span>
      </div>
    </footer>
  );
}
